import Alpine from "alpinejs";
import collapse from "@alpinejs/collapse";
import mask from "@alpinejs/mask";
import anchor from "@alpinejs/anchor";
import intersect from "@alpinejs/intersect";

// 1. Initial configuration
Alpine.plugin(collapse);
Alpine.plugin(mask);
Alpine.plugin(anchor);
Alpine.plugin(intersect);

// Expose globally for inline scripts
(window as any).Alpine = Alpine;

// 2. Shared Components
import deliverableGallery from "./deliverable-gallery";
Alpine.data("deliverableGallery", deliverableGallery);

import selectionGallery from "./selection-manager";
Alpine.data("selectionGallery", selectionGallery);

import { reviewList } from "./review-list";
Alpine.data("reviewList", reviewList);

import {
  categoryRatingChart,
  scoreVolumeChart,
  platformMiniChart,
  themeComparisonChart,
  guestTypeDistChart,
  guestTypeByMonthChart,
  stayDurationChart,
  originCountriesChart,
  seasonalDurationChart,
  seasonalCountriesChart,
} from "./review-charts";
Alpine.data("categoryRatingChart", categoryRatingChart);
Alpine.data("scoreVolumeChart", scoreVolumeChart);
Alpine.data("platformMiniChart", platformMiniChart);
Alpine.data("themeComparisonChart", themeComparisonChart);
Alpine.data("guestTypeDistChart", guestTypeDistChart);
Alpine.data("guestTypeByMonthChart", guestTypeByMonthChart);
Alpine.data("stayDurationChart", stayDurationChart);
Alpine.data("originCountriesChart", originCountriesChart);
Alpine.data("seasonalDurationChart", seasonalDurationChart);
Alpine.data("seasonalCountriesChart", seasonalCountriesChart);

import {
  analyticsTrendChart,
  analyticsBreakdownBar,
  analyticsDualAxis,
  analyticsFunnel,
  analyticsHeatmap,
  analyticsSparklineChart,
  analyticsRevenueChart,
  analyticsCampaignBar,
} from "./analytics-charts";
Alpine.data("analyticsTrendChart", analyticsTrendChart);
Alpine.data("analyticsBreakdownBar", analyticsBreakdownBar);
Alpine.data("analyticsDualAxis", analyticsDualAxis);
Alpine.data("analyticsFunnel", analyticsFunnel);
Alpine.data("analyticsHeatmap", analyticsHeatmap);
Alpine.data("analyticsSparklineChart", analyticsSparklineChart);
Alpine.data("analyticsRevenueChart", analyticsRevenueChart);
Alpine.data("analyticsCampaignBar", analyticsCampaignBar);

import { scheduleBuilder } from "./schedule-builder";
Alpine.data("scheduleBuilder", scheduleBuilder);

import { websiteBuildStatus } from "./website-build-status";
Alpine.data("websiteBuildStatus", websiteBuildStatus);

import { wizardGenerationProgress } from "./wizard-generation-progress";
Alpine.data("wizardGenerationProgress", wizardGenerationProgress);

// Area enrichment pipeline progress panel (wizard Generate step, Task 16) —
// one instance per scoped area, see area-timeline.ts's doc comment.
import { areaPipelineTimeline, areaPipelineSection } from "./area-timeline";
Alpine.data("areaPipelineTimeline", areaPipelineTimeline);
Alpine.data("areaPipelineSection", areaPipelineSection);

// Shared icon-picker STORE — ONE dialog (@IconPickerModal) per page, opened from
// any trigger (@IconTriggerButton) via $store.iconPicker. Used by the property-hub
// custom-amenity form and the CMS trust-badge repeater. A store (not a per-trigger
// component) is what makes a single modal serve every trigger with no teleport.
import { iconPickerStore } from "./icon-picker";
Alpine.store("iconPicker", iconPickerStore());

// 3. Shared Stores & Directives
import {
  fetchWithOOB,
  processOOBSwaps,
  registerGlobalHTMXListeners,
  setupCSRF,
} from "./alpine-core-registrations"; // This will be renamed from alpine-core.ts
import { TaskLinker } from "./task-linker";
import { formatBytes, selectFiles } from "./utils";

export { fetchWithOOB, formatBytes, selectFiles };
// processOOBSwaps and mergeAttractionsFill are consumed ONLY via
// window.StudioShared.* below. In the IIFE build, esbuild drops an imported
// binding whose sole use is a `window.X = binding` side-effect assignment
// (the assignment survives but the binding resolves to undefined at runtime),
// so those two StudioShared methods silently become undefined. Re-exporting
// them from the entry keeps the live binding — exactly like the three above.
export { processOOBSwaps };
// studioConfirm / boostedNavigate: exposed as StudioShared.confirm / .navigate
// for inline templ scripts (e.g. post_form.templ's deletePost). The IIFE build
// assigns this entry's EXPORTS to window.StudioShared (overwriting the object
// built below), so the export NAMES must match the StudioShared property names.
import { studioConfirm } from "./studio-confirm";
import { boostedNavigate } from "./navigate";
import { pushSentFilterURL } from "./sent-filter-url";
export { studioConfirm as confirm, boostedNavigate as navigate, pushSentFilterURL };

// Expose Studio namespace globally
(window as any).Studio = (window as any).Studio || {};
(window as any).Studio.TaskLinker = TaskLinker;

// Expose as util for Alpine templates
(window as any).util = (window as any).util || {};
(window as any).util.formatBytes = formatBytes;
(window as any).util.selectFiles = selectFiles;

// Also expose as StudioShared for some templates (will unify later)
(window as any).StudioShared = (window as any).StudioShared || {};
(window as any).StudioShared.formatBytes = formatBytes;
(window as any).StudioShared.fetchWithOOB = fetchWithOOB;
(window as any).StudioShared.processOOBSwaps = processOOBSwaps;
(window as any).StudioShared.selectFiles = selectFiles;
(window as any).StudioShared.mergeAttractionsFill = mergeAttractionsFill;
(window as any).StudioShared.confirm = studioConfirm;
(window as any).StudioShared.navigate = boostedNavigate;
(window as any).StudioShared.pushSentFilterURL = pushSentFilterURL;

// Knowledge sync status Alpine component factory (conversation sync)
import { knowledgeSyncStatus } from "./sync-status";
(window as any).knowledgeSyncStatus = knowledgeSyncStatus;

// Generic knowledge-base sync button Alpine component factory
import { knowledgeSyncButton } from "./knowledge-sync-button";
Alpine.data("knowledgeSyncButton", knowledgeSyncButton);

// 3. Editor Stores & Components
// Note: These will be imported but only if they find window.Alpine (which is now set)
import "../editor/alpine-editor";
import "../editor/main-registrations"; // This will be renamed from main.ts
import "./object_create";

// 4. Chat Editor (Tiptap with @ mentions)
import { ChatEditorComponent } from "../editor/chat-editor";
Alpine.data("chatEditor", ChatEditorComponent);

// 5. Markdown Editor
import { MarkdownEditorComponent } from "../markdown-editor/src/alpine-component";
Alpine.data("markdownEditor", MarkdownEditorComponent);

// 5.0 Gallery description template picker (deliverable create modal) — inserts a
// saved template's markdown via the editor's `md-set-content` primitive, routing
// through the themed `studio-confirm` modal once the editor holds real user work.
// The bundle has no client-side i18n accessor, so the replace-confirm message is
// passed in pre-translated as the 4th constructor arg (see project_detail.templ's
// ddtPickerXData — matches the pattern used by e.g. deliverableGroupItem).
Alpine.data("ddtPicker", (templates: any[], uiLang: string, startPristine: boolean, replaceConfirmMsg: string) => ({
  templates: templates || [],
  pristine: !!startPristine, // true = editor holds only untouched default/empty content
  get ordered() {
    return [...this.templates].sort((a, b) =>
      (a.language === uiLang ? 0 : 1) - (b.language === uiLang ? 0 : 1));
  },
  init() {
    // markdown-change bubbles from the hidden input; keydown covers the sub-100ms
    // suppress edge (alpine-component.ts:54-64). Either real interaction ends pristine.
    const el = (this as any).$el as HTMLElement;
    el.addEventListener("markdown-change", () => { this.pristine = false; });
    el.querySelector('[x-ref="editorContainer"]')
      ?.addEventListener("keydown", () => { this.pristine = false; });
  },
  apply(id: string, evt?: Event) {
    if (!id) return;
    const tpl = this.templates.find((t: any) => t.id === id);
    if (!tpl) return;
    // Resolve the editor container from the LIVE event target, not this.$el.
    // After an app-level Alpine re-init of a subtree whose <select> DOM node
    // survived, the select's @change closure can hold a STALE ddtPicker scope
    // whose $el is a detached wrapper — so this.$el.querySelector(...) returns
    // a detached container and the md-set-content dispatch hits no listener,
    // silently no-op'ing the picker (~50% race). The event target is always
    // the live node in the live DOM, so resolve the container from it.
    const scope =
      (evt && (evt.target as HTMLElement)?.closest("[x-data]")) ||
      ((this as any).$el as HTMLElement);
    const container = scope?.querySelector('[x-ref="editorContainer"]');
    const fill = () => container?.dispatchEvent(new CustomEvent("md-set-content", { detail: { markdown: tpl.content } }));
    if (this.pristine) { fill(); return; } // stays pristine: applied text is not user work
    document.dispatchEvent(new CustomEvent("studio-confirm", {
      detail: { message: replaceConfirmMsg, onConfirm: fill },
    }));
  },
}));

// 5.1 Legal AGB editor (reactive marker count + checkbox enable state)
import { legalAGBEditor } from "./legal-agb-editor";
Alpine.data("legalAGBEditor", legalAGBEditor);

// 5.2 CMS editor controller (per-field autosave, preview refresh, publish/discard)
import { cmsEditor } from "../cms/editor";
Alpine.data("cmsEditor", cmsEditor);

// 5.2b Stand-in scope for CMS pages with a target switcher but no editable fields
// (area-guide empty state): supplies flushPending/_blockOnInvalidPlaceholders.
import { cmsNoEditor } from "../cms/no-editor";
Alpine.data("cmsNoEditor", cmsNoEditor);

// 5.2-store CMS editor device toggle — GLOBAL store, not cmsEditor component state.
// The section-preview iframe lives inside the #cms-editor-main HTMX swap target and
// its device-sizing :class is re-evaluated by the editor's contingency Alpine.initTree
// after each rail swap, in a scope that does NOT reach cmsEditor component vars. A
// global store ($store.cmsDevice) is resolvable in any Alpine evaluation regardless
// of data stack, so the binding can never throw "device is not defined".
Alpine.store("cmsDevice", { value: "desktop" as "desktop" | "tablet" | "mobile" });

// 5.2b-store CMS editor active-section mirror — GLOBAL store, discoverable global
// section state. cmsEditor mirrors `section` into it (init + $watch). NOTE: the
// preview pane's per-surface layout/visibility is NO LONGER driven by reactive
// $store.cmsSection bindings — it's a server-rendered data-cms-section attribute on
// #cms-editor-preview + CSS (_cms-editor.css), which editor.ts updates imperatively
// on rail nav (race-free; a reactive x-show could evaluate before the store seed on
// the hx-boost body swap). This store is kept synced as the canonical global value
// but nothing reactive on the pane reads it.
Alpine.store("cmsSection", { value: "" as string });

// 5.2c-store CMS preview Light/Dark — GLOBAL store, same rationale as cmsSection:
// the toolbar active-state AND the apply both read this single value, so they can
// never diverge across swaps/re-inits. Seeded from localStorage; the toggle writes
// here + localStorage + the iframe attribute.
Alpine.store("cmsPreviewTheme", {
  value:
    typeof localStorage !== "undefined" && localStorage.getItem("cms_preview_theme") === "dark"
      ? "dark"
      : "light",
});

// 5.2d-store CMS preview iframe URL — GLOBAL store, same rationale as cmsSection/
// cmsDevice. The persistent iframe's :src is re-evaluated by the contingency
// Alpine.initTree after every rail swap; a baked static URL there reverts the frame
// to the section it was first rendered with, so section-mode rail nav "never
// updates". Reading this store makes the re-eval resolve the CURRENT section URL, and
// writing it reactively reloads the frame. Seeded + updated by cmsEditor.
// section: the CMS editor section `value` was set for (editor-preview.ts
// _seedPreviewURLStore — tells a same-section re-init from a section change
// on the section-less full-page booking preview URL).
Alpine.store("cmsPreviewURL", { value: "" as string, section: "" as string });

// 5.2e-store CMS version-preview — GLOBAL, same rationale as cmsSection/cmsPreviewURL.
// The read-only banner (#cms-editor-preview), the iframe :src, and the leftpane
// :inert read this; the version panel (HTMX-swapped into the aside) writes it. As a
// component var it threw "versionPreview is not defined" under the post-swap
// Alpine.initTree, and reset on every body swap (page/mode switch) — silently
// dropping the preview. pubId (not a full URL) is stored; the iframe :src derives
// the URL from $store.cmsPreviewURL + '&publication=' so the preview FOLLOWS nav.
Alpine.store("cmsVersionPreview", { active: false, pubId: "" as string, date: "" as string });

// CMS page-switch navigation state — GLOBAL so the loading overlay survives the
// full-<body> innerHTML swap that a page switch performs (target switcher / boosted
// /edit links / popstate). Set true when an htmx request targeting <body> starts
// and cleared when it settles (see editor.ts's body-level htmx listeners). Drives
// the #cms-nav-overlay shell overlay so the multi-second page-switch wait (server
// re-render + full-page preview render) is never a blind dead click. A component
// var would reset on the swap; a store persists.
Alpine.store("cmsNav", { navigating: false });

// 5.2f-store CMS version-restore in-flight — GLOBAL. The version panel's :class and
// the Restore button's @htmx:before/after-request read/write this; under the
// post-swap initTree a component var threw "versionRestoring is not defined" (the
// reported console error). Disables the panel while a restore POST is in flight.
Alpine.store("cmsVersionRestoring", { value: false });

// 5.2g-store CMS assistant panel open/closed (Task 6.2, design spec §10) —
// GLOBAL, same rationale as cmsSection/cmsVersionPreview: the header toggle
// button (never itself swapped) and the leftpane/assistant-panel bindings
// inside #cms-editor-main (which ARE re-initialized by the post-swap
// Alpine.initTree after every #cms-editor-leftpane rail swap) must always
// agree on one value, which only a global store guarantees across re-inits.
Alpine.store("cmsAssistant", { open: false });

// 5.2h-store CMS mobile edit/preview tab + POI manage-mode takeover — GLOBAL,
// same rationale as the other CMS stores above: #cms-editor-leftpane is an
// HTMX outerHTML swap target, so a component var wouldn't resolve there.
// `tab` drives the <lg region gate (Task 2's tab bar; no UI yet). `manageMode`
// replaces the old local x-data on #cms-editor-main (T2.1/R8) — lifting it to
// a store means the POI board takeover survives rail swaps AND cross-page
// body swaps (which rebuild #cms-editor-main) don't depend on that subtree's
// x-data init for its state or reactivity. The body swap reset that used to come "for free"
// from destroy+init of that local x-data now must be explicit — see editor.ts's
// htmx:afterSettle body swap branch, which calls reset() there (R9: rail
// swaps must NOT reset, only cross-page navigation).
Alpine.store("cmsMobileTab", {
  tab: "edit" as "edit" | "preview",
  manageMode: false,
  setTab(name: "edit" | "preview") {
    this.tab = name;
  },
  // POI manage is a preview-slot takeover: entering jumps to the preview tab,
  // exiting returns to edit. At lg+ the tab half is a visual no-op.
  enterPOIManage() {
    this.manageMode = true;
    this.tab = "preview";
  },
  exitPOIManage() {
    this.manageMode = false;
    this.tab = "edit";
  },
  reset() {
    this.tab = "edit";
    this.manageMode = false;
  },
  // Cross-PAGE nav (editor.ts's genuineNav body swap branch) must clear the
  // R9 manage-mode takeover (don't carry a POI-board takeover onto a
  // different page) WITHOUT resetting which tab (Edit/Preview) the operator
  // was on — unlike reset(), which also forces tab back to 'edit' and used
  // to yank Preview-tab operators back to Edit on every page switch.
  resetManageMode() {
    this.manageMode = false;
  },
});

// 5.2i-store CMS section-drawer open/closed (Task 4) — GLOBAL, mirrors
// $store.cmsAssistant / $store.wizardAssistant: the section rail (a static
// left column at lg+, an off-canvas left drawer below lg — see
// .offcanvas-rail--left in cms_editor.templ) and its header trigger button
// are DOM siblings, so only a global store keeps them in sync.
Alpine.store("cmsSectionDrawer", {
  open: false,
  toggle() {
    this.open = !this.open;
  },
  close() {
    this.open = false;
  },
});

// 5.2a CMS editor-native image widget (instance-scoped picker, draft-writing)
import { cmsImagePicker } from "../cms/image-picker";
Alpine.data("cmsImagePicker", cmsImagePicker);

// 5.2a-print Print configurator rail's single-image picker (Task 7c) — a
// slim, read-only, single-select sibling of cmsImagePicker: no autosave, no
// upload, no remove-from-pool. See static/js/promote/print-image-picker.ts.
import { printImagePicker } from "../promote/print-image-picker";
Alpine.data("printImagePicker", printImagePicker);

// 5.2a-v CMS hero video widget (pool_video uploader + live transcode SSE)
import { cmsVideoPicker } from "../cms/video-picker";
Alpine.data("cmsVideoPicker", cmsVideoPicker);

// 5.2b CMS "Images" caption surface (list + lightbox-style live preview)
import { cmsImageCaptions } from "../cms/image-captions";
Alpine.data("cmsImageCaptions", cmsImageCaptions);

// 5.2b-credits Shared image-credit save controller (progressive-disclosure
// author/license/source form, cms_credit_fields.templ) — consumed by the
// legal-section credit list and the CMS image-picker (both later tasks).
import { cmsImageCredits } from "../cms/image-credits";
Alpine.data("cmsImageCredits", cmsImageCredits);

// 5.2c CMS repeater widget (editable list of structured rows, e.g. host_values
// {icon,text}; add/remove/reorder; emits the rows array on cms-image-changed)
import { cmsRepeater } from "../cms/repeater";
Alpine.data("cmsRepeater", cmsRepeater);

// 5.2c1 CMS surrounding-picker widget (property location: pick/reorder canonical
// surroundings + per-place icon via the shared icon picker; emits
// featured_surrounding_ids + surroundings_icons on cms-image-changed)
import { cmsSurroundingPicker } from "../cms/surrounding-picker";
Alpine.data("cmsSurroundingPicker", cmsSurroundingPicker);

// 5.2c2 CMS amenity-grouping widget (organise the property amenity pool into
// labelled tabs; pure-data core: add/remove/reorder tabs + move-▾ menu for chips,
// legacy {categories[]} tabs expanded to {amenity_ids[]} on load; emits the tabs
// array on cms-image-changed. SortableJS drag is layered on in a follow-up task)
import { cmsAmenityGrouping } from "../cms/amenity-grouping";
Alpine.data("cmsAmenityGrouping", cmsAmenityGrouping);

// T2.8a CMS POI board widget (area Discover section takeover pane: ordered,
// drag-reorderable card list over the area's discover facts; drag emits the
// whole-value fact-id array on cms-image-changed to the discover.order
// overlay. Per-site prose/hide (T2.8c) and org-shared category/geo/image/
// delete (T2.8b) land in later slices)
import { cmsPOIBoard } from "../cms/poi-board";
Alpine.data("cmsPOIBoard", cmsPOIBoard);

// T2.10 CMS area envelope-image slot widget (hero/intro/seasonal org-scoped
// images on the area page: wraps cmsImagePicker with an org-write chokepoint
// straight to AreaService.SetEnvelopeImageSlot's endpoint, same divergence as
// the POI board's org-shared image slot, T2.9)
import { cmsAreaEnvelopeImageSlot } from "../cms/area-envelope-images";
Alpine.data("cmsAreaEnvelopeImageSlot", cmsAreaEnvelopeImageSlot);

// 5.2c3 CMS review-picker widget (property reviews: transactional click-add from
// the scraped review pool + reorderable selected list; emits the ordered
// featured_review_ids array on cms-image-changed on confirm)
import { cmsReviewPicker } from "../cms/review-picker";
Alpine.data("cmsReviewPicker", cmsReviewPicker);

// Task B9 CMS area-poi-picker widget (area homepage_teaser section: pick UP
// TO 2 of the area's own discover-fact POIs to feature on the homepage area
// card; transactional click-add from the area's discover-fact pool +
// reorderable selected list; emits the ordered card_poi_ids string[] on
// cms-image-changed on confirm; MAX 2)
import { cmsAreaPOIPicker } from "../cms/poi-picker";
Alpine.data("cmsAreaPOIPicker", cmsAreaPOIPicker);

// 5.2c3b CMS praise-picker widget (homepage testimonials: transactional
// click-add from the site-wide extracted praise-string pool + reorderable
// selected chips; emits the ordered top_praises string[] on cms-image-changed
// on confirm)
import { cmsPraisePicker } from "../cms/praise-picker";
Alpine.data("cmsPraisePicker", cmsPraisePicker);

// 5.2c3c CMS theme-quote-picker widget (homepage testimonials: transactional
// click-add from the site-wide extracted {theme,quote} pool + reorderable
// selected cards; emits the ordered theme_quotes [{theme,quote}][] on
// cms-image-changed on confirm; MAX 4)
import { cmsThemeQuotePicker } from "../cms/theme-quote-picker";
Alpine.data("cmsThemeQuotePicker", cmsThemeQuotePicker);

// 5.2c4 CMS property-cards widget (site-level: reorderable list of property
// cards; each row opens a per-card modal editing teaser + inline image grid +
// featured-amenities repeater + personas. Per-card Done emits the WHOLE card to
// property:card:<slug> (transactional); reorder emits the slug array to
// site:property_order on cms-image-changed immediately)
import { cmsPropertyCards } from "../cms/property-cards";
Alpine.data("cmsPropertyCards", cmsPropertyCards);

// 5.2c5 CMS section rail (reorder + show/hide). Reads the live order + per-row
// visibility from the server-rendered rail DOM and emits the full section-order
// array to SectionsTarget via cms-image-changed (editor.ts autosave path).
import { cmsSectionRail } from "../cms/section-rail";
Alpine.data("cmsSectionRail", cmsSectionRail);

// 5.2c6 CMS other-properties widget (property-level: reorderable list of the
// site's OTHER properties with per-row eye toggles; emits the ordered
// [{slug,enabled}] to property:sections.other_properties on every drag/toggle)
import { cmsOtherProperties } from "../cms/other-properties";
Alpine.data("cmsOtherProperties", cmsOtherProperties);

// 5.2c7 CMS area-cards widget reorder (homepage areas section: drag handles on
// the server-rendered per-area card rows; reads the live slug order from the
// DOM — like cmsSectionRail, not a JSON-blob controller — and emits it to
// site:homepage.areas field "order" on every drop)
import { cmsAreaCardsReorder } from "../cms/area-cards";
Alpine.data("cmsAreaCardsReorder", cmsAreaCardsReorder);

// 5.2d Property-hub curation modal (chip-filtered two-pane reconcile of the
// canonical image set; registers itself in the `curation` Alpine store)
import { propertyCurationModal } from "../cms/curation-modal";
Alpine.data("propertyCurationModal", propertyCurationModal);

// 5.2e Property-hub images section wrapper (activeTab + editPano media-kind
// toggle — PropertyCurationModal nests inside this scope and inherits
// editPano from it via Alpine's ordinary nested-x-data fallback)
import { propertyImagesHub } from "../cms/property-images-hub";
Alpine.data("propertyImagesHub", propertyImagesHub);

// 5. Wiki Autosave
import { wikiAutosave } from "./wiki-autosave";
Alpine.data("wikiAutosave", wikiAutosave);

// 5. Wizard Autosave (per-field + per-section + selection-checkbox + location factories)
// mergeAttractionsFill is exposed on window.StudioShared below and consumed by the
// wizard step-7 location form's inline SSE listener — it MUST stay imported or the
// live POI update silently breaks (esbuild strips a missing import without error).
import {
  wizardAutosave,
  wizardSectionAutosave,
  wizardSelectionAutosave,
  propertyLocationAutosave,
  addAccommodationLocation,
  mergeAttractionsFill,
} from "./wizard-autosave";
// Re-exported so esbuild keeps the live binding for the window.StudioShared
// assignment above (see the note on the export block near the top of this file).
export { mergeAttractionsFill };
Alpine.data("wizardAutosave", wizardAutosave);
Alpine.data("wizardSectionAutosave", wizardSectionAutosave);
Alpine.data("wizardSelectionAutosave", wizardSelectionAutosave);
Alpine.data("propertyLocationAutosave", propertyLocationAutosave);
Alpine.data("addAccommodationLocation", addAccommodationLocation);

// Shared language picker (wizard step 1 + website management → Languages):
// primary/additional coupling plus the plan's language cap.
import { languagePicker } from "./language-picker";
Alpine.data("languagePicker", languagePicker);

// Reassign (PMS apartment binding) confirmation modal. Registered here — not
// via an inline alpine:init listener in the templ — because the booking-widget
// section is lazy-loaded via HTMX (?partial=content&section=booking), which
// injects the x-data="reassignConfirm" markup after Alpine has already started.
import { reassignConfirm } from "./reassign-confirm";
Alpine.data("reassignConfirm", reassignConfirm);

// 5. Content Pool Store & Components
import { registerContentPoolStore, contentPoolGrid } from "./content-pool";
registerContentPoolStore(Alpine);
Alpine.data("contentPoolGrid", contentPoolGrid);

// 5a. Create Page sidebar store (simple boolean toggle, defaults open on desktop)
Alpine.store("createSidebar", window.innerWidth >= 1024);

// 5b. Content Generation Listeners
import { setupContentGenerationListeners } from "./content-generation";
setupContentGenerationListeners();

// 5b1. Listing Export (property detail drawer "Copy JSON" button)
import "./listing-export";

// 5e1. Listing Preview Picker (Embla carousel + slot picker for listing platforms)
import { registerListingPreview } from "./listing-preview";
registerListingPreview(Alpine);

// 5e2. Deliverable Reorder (project page drag-drop sort)
import { registerDeliverableReorder } from "./deliverable-reorder";
registerDeliverableReorder(Alpine);

// 5f. Toast store
import { toastStore, registerFlashCookieBridge } from "./toast";
Alpine.data("toastStore", toastStore);

// 5f-bis. Top-layer tooltip (escapes overflow-clipping via the Popover API)
import { topLayerTip } from "./toplayer-tip";
Alpine.data("topLayerTip", topLayerTip);

// 5g. iOS shell adaptations (no-op in Safari / desktop; gates on the
// "StudioApp/1.0" UA marker set by the iOS WebView wrapper).
import "./ios-shell";

// 5. Start Studio UI
const init = () => {
  // 1. Setup CSRF protection (before HTMX init)
  setupCSRF();
  // 2. Initialize global HTMX error handling (requires document.body)
  registerGlobalHTMXListeners();
  // 3. Start Alpine
  Alpine.start();
  // 4. Re-read the one-shot flash cookie after htmx boost navigations (the
  // hx-preserve'd toast store's init() only fires on a full page load). Registered
  // here (DOM-ready) so it never runs before the document/htmx are available.
  registerFlashCookieBridge();
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}

// --- Global-exposure self-check ------------------------------------------
// The window.* assignments above copy imported bindings onto global namespaces
// that inline templates and SSE listeners call by name. esbuild can silently
// leave such a binding `undefined` when its ONLY consumer is a `window.X =
// binding` side-effect AND its source module sits in an import cycle (the
// binding is read here before its lazily-initialised module has run). That is
// invisible to tsc and to the build — it only surfaces as a broken feature at
// runtime. See the processOOBSwaps / mergeAttractionsFill incident and the
// re-export note near the top of this file. Assert loudly (deferred a microtask
// so all synchronous module init has completed) so a regression is caught the
// instant a page loads instead of shipping silently. A regression also fails
// CI via scripts/verify-bundle-globals.mjs.
queueMicrotask(() => {
  const w = window as any;
  const required: Array<[string, unknown]> = [
    ["StudioShared.formatBytes", w.StudioShared?.formatBytes],
    ["StudioShared.fetchWithOOB", w.StudioShared?.fetchWithOOB],
    ["StudioShared.processOOBSwaps", w.StudioShared?.processOOBSwaps],
    ["StudioShared.selectFiles", w.StudioShared?.selectFiles],
    ["StudioShared.mergeAttractionsFill", w.StudioShared?.mergeAttractionsFill],
    ["StudioShared.confirm", w.StudioShared?.confirm],
    ["StudioShared.navigate", w.StudioShared?.navigate],
    ["StudioShared.pushSentFilterURL", w.StudioShared?.pushSentFilterURL],
    ["util.formatBytes", w.util?.formatBytes],
    ["util.selectFiles", w.util?.selectFiles],
    ["Studio.TaskLinker", w.Studio?.TaskLinker],
    ["knowledgeSyncStatus", w.knowledgeSyncStatus],
    ["pricingGroupNeighbor", w.pricingGroupNeighbor],
    ["pricingOccupancy", w.pricingOccupancy],
  ];
  const missing = required.filter(([, v]) => v == null).map(([k]) => k);
  if (missing.length) {
    console.error(
      "[studio-bundle] GLOBAL EXPOSURE CHECK FAILED — these window globals are " +
        "undefined: " +
        missing.join(", ") +
        ". Likely an esbuild cyclic-import drop; re-export the binding from " +
        "studio-bundle.ts (see the note at the top of the file).",
    );
  }
});
